/* import __COLOCATED_TEMPLATE__ from './school-selector.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type CurrentUserService from 'dashboard/services/current-user';
import type { School } from 'dashboard/types';
import type RouterService from '@ember/routing/router-service';
import { guidFor } from '@ember/object/internals';

interface SchoolSelectorSignature {
  Element: HTMLDivElement;
  Args: {
    buttonClass: string;
    dropdownClass: string;
    isMobile: boolean;
    currentSchool?: School;
    schools: School[];
  };
}

type SchoolForDropdown = Pick<School, 'name' | 'id' | 'district' | 'type'>;

interface SchoolWithChildren extends SchoolForDropdown {
  children?: SchoolForDropdown[];
}

function pluckSchoolProps(school: School): SchoolForDropdown {
  return {
    id: school.id,
    name: school.name,
    district: school.district,
    type: school.type,
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    SchoolSelector: typeof SchoolSelector;
  }
}

export default class SchoolSelector extends Component<SchoolSelectorSignature> {
  @service declare currentUser: CurrentUserService;
  @service declare router: RouterService;
  @tracked isOpen = false;
  @tracked search = '';

  selectorListId = 'school-selector-list-' + guidFor(this);

  get results(): SchoolWithChildren[] | undefined {
    if (this.search) {
      return this.flatResults;
    } else {
      // Show results with children
      const results = [...this.args.schools];
      return results
        .map((school) => {
          // Go through each result and nest any child results
          const result: SchoolWithChildren = pluckSchoolProps(school);

          const childResults = results.filter(
            (child) => child.district === school.id
          );
          if (childResults.length > 0) {
            result.children = childResults.map(pluckSchoolProps);
          }
          return result;
        })
        .filter((school) => {
          // Display any remaining purchasers that don't have a district ID,
          // or whose district doesn't exist in the list of purchasers
          return (
            school.district === '0' ||
            results.findIndex((result) => school.district === result.id) === -1
          );
        });
    }
  }

  get flatResults(): School[] {
    return [...this.args.schools].filter((school) =>
      school.name.toLocaleLowerCase().includes(this.search.toLocaleLowerCase())
    );
  }

  @action handleToggleClick(e: MouseEvent): void {
    e.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  @action close(e: Event): void {
    e.stopPropagation();
    this.isOpen = false;
  }

  @action focus(element: HTMLElement): void {
    element.focus();
  }

  @action handleSearch(event: InputEvent): void {
    const target = event.target as HTMLInputElement;
    if (target) {
      this.search = target.value;
    }
  }

  @action handleClick(e: MouseEvent): void {
    if (!this.args.isMobile) {
      this.isOpen = false;
    }
    e.preventDefault();

    const urlOriginal = new URL((e.currentTarget as HTMLAnchorElement).href);
    // remove pagination values to avoid invalid cursor errors
    // (these params will not apply to the new purchaser context)
    urlOriginal.searchParams.delete('after');
    urlOriginal.searchParams.delete('before');

    const url = urlOriginal.href.replace(urlOriginal.origin, '');

    if (this.router.recognize(url)) {
      this.router.transitionTo(url);
    }
  }
}
